/**
 * Settings > Statements live preview helpers (StatementPreviewPane.vue).
 *
 * The backend renders every sample statement kind and returns a fingerprint per kind. Comparing
 * the fingerprints from before and after an edit tells us which kinds the edit actually changed,
 * so the warning stays truthful as the statement layout logic changes. There is no hand-kept
 * "setting -> statement" table to fall out of date.
 */

/** Fields that only shape the email sent with emailed statements, never the PDF itself. */
export const EMAIL_FIELDS = ['bccEmailAddress', 'subjectLine', 'subjectBody'];

/** The email preview is drawn in the browser, so it is not one of the backend's kinds. */
export const EMAIL_KIND = {
  key: 'email',
  label: 'Email Message',
  description: 'The email sent with emailed statements.',
};

/** Fields that never change a statement and should not be sent for, or count as, an edit. */
const IGNORED_FIELDS = [
  'id',
  'pidValue',
  'savedNormalMessages',
  'savedDelinquentMessages',
  'savedUnderGridMessages',
  'savedNextToGridMessages',
];

const same = (a, b) => JSON.stringify(a === undefined ? null : a)
  === JSON.stringify(b === undefined ? null : b);

/** Keys whose values differ between two settings snapshots, ignoring bookkeeping fields. */
export const changedFields = (prev = {}, next = {}) => {
  const keys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
  return [...keys]
    .filter((key) => !IGNORED_FIELDS.includes(key))
    .filter((key) => !same((prev || {})[key], (next || {})[key]));
};

/** The settings the backend renders from: everything except bookkeeping and email-only fields. */
export const previewRequestConfig = (config = {}) => {
  const out = {};
  Object.keys(config || {}).forEach((key) => {
    if (!IGNORED_FIELDS.includes(key) && !EMAIL_FIELDS.includes(key)) out[key] = config[key];
  });
  return out;
};

/** Keys of the kinds whose fingerprint differs between two renders. */
export const changedKinds = (prevKinds = [], nextKinds = []) => {
  const before = {};
  (prevKinds || []).forEach((k) => { before[k.key] = k.fingerprint; });
  return (nextKinds || [])
    .filter((k) => before[k.key] !== undefined && before[k.key] !== k.fingerprint)
    .map((k) => k.key);
};

const joinLabels = (labels) => {
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/**
 * The note under the preview after an edit, or null when the edit shows on the statement
 * being viewed.
 *
 * @param {string} currentKey kind being viewed
 * @param {string[]} affected kind keys the last edit changed
 * @param {Array<{key: string, label: string}>} kinds every kind, for labels
 */
export const impactWarning = (currentKey, affected, kinds) => {
  if (!affected || affected.includes(currentKey)) return null;
  if (affected.length === 0) {
    return 'This change doesn\'t affect this statement or any other statement kind with your '
      + 'current settings. It may only take effect together with another setting.';
  }
  const labels = affected
    .filter((key) => key !== EMAIL_KIND.key)
    .map((key) => {
      const kind = (kinds || []).find((k) => k.key === key);
      return kind ? kind.label : key;
    });
  const parts = [];
  if (labels.length > 0) {
    parts.push(`the ${joinLabels(labels)} ${labels.length === 1 ? 'statement' : 'statements'}`);
  }
  // The email isn't a statement, so it gets its own phrase.
  if (affected.includes(EMAIL_KIND.key)) parts.push('the email sent with emailed statements');
  return `This setting won't affect this statement, but it does affect ${parts.join(' and ')}.`;
};

/**
 * Fixed sentences an option prints, which a county may reword. Blank means the default.
 * `defaultText` mirrors STATEMENT_TEXT_DEFAULTS in the backend's printStatement/statementText.js;
 * change both together. `maxLength` keeps the text inside the space the renderer gives it.
 */
export const STATEMENT_TEXT_FIELDS = {
  retainThisStub: {
    textKey: 'retainThisStubText',
    title: '"Retain this stub" line',
    defaultText: 'Retain this portion for your records, or when paying in person bring entire statement',
    maxLength: 110,
    placeholders: [],
  },
  includeAddressChangeBox: {
    textKey: 'addressChangeBoxText',
    title: 'Address change box label',
    defaultText: 'Enter Address Changes Here',
    maxLength: 34,
    placeholders: [],
  },
  printTaxBecomesDelinquent: {
    textKey: 'taxBecomesDelinquentText',
    title: '"Tax becomes delinquent" notice',
    defaultText: 'Tax Becomes Delinquent January 1st unless paid in Full or Half Late PENALTY is {penaltyRate} per month until paid',
    maxLength: 160,
    placeholders: [{ token: '{penaltyRate}', meaning: 'the penalty rate from Tax settings, e.g. "1.5 %"' }],
  },
};

/** The wording that will print: the county's text, or the default when blank. */
export const effectiveStatementText = (config, optionKey) => {
  const field = STATEMENT_TEXT_FIELDS[optionKey];
  const custom = typeof (config || {})[field.textKey] === 'string' ? config[field.textKey].trim() : '';
  return custom || field.defaultText;
};

export const isStatementTextCustomized = (config, optionKey) => {
  const field = STATEMENT_TEXT_FIELDS[optionKey];
  const custom = typeof (config || {})[field.textKey] === 'string' ? config[field.textKey].trim() : '';
  return custom !== '' && custom !== field.defaultText;
};

/* --- form helpers: the per-stub code selector stands in for six checkboxes --- */

export const STUB_POSITIONS = ['Top', 'Middle', 'Bottom'];

/**
 * The stub that has room for a QR code and a barcode together. The middle and bottom stubs
 * print one or the other.
 */
export const BOTH_CODES_STUB = 'Top';

/**
 * 'barcode' | 'qr' | 'both' | 'none' for one stub, matching what the renderer prints: the top stub
 * prints both when both flags are set; the middle and bottom stubs print the barcode.
 */
export const stubCode = (config, position) => {
  const qr = !!config[`printQR${position}`];
  const barcode = !!config[`printBarcode${position}`];
  if (qr && barcode) return position === BOTH_CODES_STUB ? 'both' : 'barcode';
  if (qr) return 'qr';
  if (barcode) return 'barcode';
  return 'none';
};

/**
 * The flag values for choosing `code` on a stub. The legacy printBarcodeNone / printQRNone /
 * printScanlineNone flags are kept in step because older screens still read them.
 */
export const stubCodeFlags = (config, position, code) => {
  const next = {
    ...config,
    [`printBarcode${position}`]: code === 'barcode' || code === 'both',
    [`printQR${position}`]: code === 'qr' || code === 'both',
  };
  return {
    [`printBarcode${position}`]: next[`printBarcode${position}`],
    [`printQR${position}`]: next[`printQR${position}`],
    printBarcodeNone: !STUB_POSITIONS.some((p) => next[`printBarcode${p}`]),
    printQRNone: !STUB_POSITIONS.some((p) => next[`printQR${p}`]),
  };
};

export const scanlineNoneFlag = (config) => !STUB_POSITIONS.some((p) => config[`printScanline${p}`]);

/** 'standard' | 'message' | 'grid'. The two flags behind it are mutually exclusive. */
export const middleSectionMode = (config) => {
  if (config.printIfPaidByGrid) return 'grid';
  if (config.printMsgInMiddle) return 'message';
  return 'standard';
};

export const middleSectionModeFlags = (mode) => ({
  printMsgInMiddle: mode === 'message',
  printIfPaidByGrid: mode === 'grid',
});
